// src/pages/Account.jsx
import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { db } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import PageContainer from '../components/PageContainer';

function Account() {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();

  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchProfile = async () => {
      if (!currentUser) {
        console.log('No current user. Redirecting to login.');
        navigate('/login');
        return;
      }

      try {
        console.log('Fetching user data for UID:', currentUser.uid);
        const docRef = doc(db, 'users', currentUser.uid);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          console.log('User data found:', docSnap.data());
          setUserData(docSnap.data());
        } else {
          console.warn('No such document!');
          setError('No profile found. Please sign up or update your details.');
        }
      } catch (err) {
        console.error('Error fetching user data:', err);
        setError('Failed to load user data.');
      } finally {
        setLoading(false);
        console.log('Loading state set to false.');
      }
    };

    fetchProfile();
  }, [currentUser, navigate]);

  const handleLogout = async () => {
    try {
      await logout();
      // Optionally navigate to '/login' after logout
      // navigate('/login');
    } catch (err) {
      console.error('Logout error:', err);
      alert('Failed to logout.');
    }
  };

  if (loading) {
    return <div className="loading">Loading your account information...</div>;
  }

  // We no longer *return* inside the error check.
  // Instead, we display error *alongside* or *above* the logout button.
  return (
    <PageContainer>
      <h2>Account Page</h2>

      {/* Show error message if present */}
      {error && (
        <p style={{ color: 'red' }}>{error}</p>
      )}

      {/* If there's user data, display it. If not, show fallback text. */}
      {!userData ? (
        <p>No user data available.</p>
      ) : (
        <>
          {/* Artist Fields */}
          {userData.role === 'artist' && (
            <div className="account-info">
              <p><strong>Role:</strong> Artist</p>
              <p><strong>Email:</strong> {userData.email}</p>
              <p><strong>Age:</strong> {userData.age}</p>
              <p><strong>Gender:</strong> {userData.gender}</p>
              <p><strong>Genre of Music:</strong> {userData.genreOfMusic}</p>
            </div>
          )}

          {/* Mixing Engineer Fields */}
          {userData.role === 'engineer' && (
            <div className="account-info">
              <p><strong>Role:</strong> Mixing Engineer</p>
              <p><strong>Email:</strong> {userData.email}</p>
              <p><strong>First Name:</strong> {userData.firstName}</p>
              <p><strong>Last Name:</strong> {userData.lastName}</p>
              <p><strong>Age:</strong> {userData.age}</p>
              <p><strong>Proficiency Level:</strong> {userData.proficiency}</p>
              <p><strong>Years of Mixing Experience:</strong> {userData.yearsExperience}</p>
              <p><strong>Institution:</strong> {userData.institution || 'N/A'}</p>
              <p><strong>Genres Experienced:</strong> {userData.genresExperienced}</p>
            </div>
          )}

          {/* If role is missing or not recognized */}
          {userData.role !== 'artist' && userData.role !== 'engineer' && (
            <p>Unknown role or no data for this user!</p>
          )}
        </>
      )}

      {/* Logout button is always present, regardless of error or missing doc */}
      <div style={{ textAlign: 'center' }}>
        <button
          className="logout-button"
          onClick={handleLogout}
          style={{ margin: '0 auto', display: 'block' }}
        >
          Logout
        </button>
      </div>
    </PageContainer>
  );
}

export default Account;
